import {isElementVisibleInViewport} from "../../helpers/element_in_viewport";
import Component from "../../lib/component";
import {polyfillRequestIdleCallback} from "../../polyfills/request_idle_callback_polyfill";
import {getThrottledFunction} from "../../helpers/throttle";

export const CollapserTargetConfig = {
    componentTypeKey: 'CollapserTarget',
    baseHtmlClass: 'collapser_target',
    selector: '.collapser_target',

    datasetConfigKey: 'collapser_target',
    identifierDatasetConfigKey: 'identifier',
    openDatasetConfigKey: 'open',
    collapsableDatasetConfigKey: 'collapsable',
};

export class CollapserTarget extends Component {

    static get config() {
        return CollapserTargetConfig;
    }

    constructor(id_or_element, collapser) {
        super({
            element: id_or_element,
            config: CollapserTarget.config
        });

        this.setDatasetConfig(CollapserTarget.config.datasetConfigKey);

        this.applyToClass = this.getDatasetConfigValue('apply_to_class');

        this.transitionDuration = this.getTransitionDuration();

        this.collapser = collapser;

        this.height = null;

        const mutationHandler = getThrottledFunction(this.render.bind(this), 100);
        this.mutationObserver = new MutationObserver(mutationHandler);
        this.mutationObserver.observe(this.element, {attributes: true, childList: true, subtree: true});

        const resizeHandler = getThrottledFunction(this.render.bind(this), 100);
        this.resizeObserver = new ResizeObserver(resizeHandler);
        this.resizeObserver.observe(this.element);

        this.renderInitial();
        this.render();

        polyfillRequestIdleCallback();

        // We unlock the height for open components so that all the content is shown (in case the calculated height is incorrect for some reasons).
        // The unlocking of the height needs a delay for unknown reason! Otherwise the absolute height sticks.
        if (this.isOpen()) {
            window.setTimeout(() => {
                window.requestIdleCallback(() => {
                    this.unlockHeightWhenOpen(); // Unlocking the height will set the height to `auto` so all content is shown.
                })
            }, this.transitionDuration);
        }
    }


    // Rendering…

    renderInitial() {
        this.element.style['transitionDuration'] = `${this.transitionDuration}ms`;
    }

    renderHeight() {
        if (!this.collapser.isCollapsable) return;
        this.element.style.height = (this.height == null) ? null : `${this.height}px`;
    }

    renderOpacity() {
        if (!this.collapser.isCollapsable) return;
        this.element.style['opacity'] = (this.isOpen() ? null : '0');
    }

    render() {
        this.height = this.getHeight();
        this.renderHeight();
        this.renderOpacity();
        this.swapHtmlClassStates(this.isOpen(), 'open', 'closed');
        if (this.applyToClass) {
            this.applyToClass.split(' ').forEach((className) => {
                if (this.isOpen()) {
                    this.element.classList.add(`${className}--open`);
                    this.element.classList.remove(`${className}--closed`);
                } else {
                    this.element.classList.add(`${className}--closed`);
                    this.element.classList.remove(`${className}--open`);
                }
            });
        }
    }


    // Toggle handling…

    toggleCollapse() {
        this.isOpen() ? this.toggleToClosed() : this.toggleToOpen();
        this.render();
    }

    toggleToClosed() {
        this.lockHeightWhenOpen(); // Before closing we lock the height to an absolute value (it might have been unlocked to `auto`) so that height animations will transition.
        this.collapser.isOpen = false;
    }

    toggleToOpen() {
        // After the rendering and animations have completed we unlock the height (just in case our height calc is wrong) and ensure the contents are in the view.
        window.setTimeout(() => {
            window.requestIdleCallback(() => {
                this.unlockHeightWhenOpen(); // Unlocking the height will set the height to `auto` so all content is shown.
                if (this.isOpen()) this.scrollContentsIntoView();
            })
        }, this.transitionDuration + 100);
        this.collapser.isOpen = true;
    }


    // Height Calcs…

    getHeight() {
        return this.isOpen() ? this.getOpenHeight() : this.getClosedHeight();
    }

    getOpenHeight() {
        let height = this.getContentsHeight();
        if (height === 0) height = null;
        return height;
    }

    getClosedHeight() {
        return 0; // TODO - This should be calculated from the toggle element when it is within the target element.
    }

    getContentsHeight() {
        let height = 0;
        for (let child of this.element.children) {
            height += child.offsetHeight;
        }
        return height;
    }


    // Height locking…

    // Remove any absolutely calculated height and set the element's height to 'auto'.
    unlockHeightWhenOpen() {
        if (this.isClosed()) return;
        this.height = null;
        this.renderHeight();
    }

    lockHeightWhenOpen() {
        if (this.isClosed()) return;
        this.height = this.getOpenHeight();
        this.renderHeight();
    }


    // Attribute helpers…

    isOpen() {
        return this.collapser.isOpen;
    }

    isClosed() {
        return !this.collapser.isOpen;
    }


    // Initialization helpers…

    // TODO - The duration comes from how much content is within the target element.
    getTransitionDuration() {
        const duration = 300;
        return Math.max(200, Math.min(600, duration)); // between 200…600 milliseconds
    }


    // Utils…

    scrollContentsIntoView() {
        const scrollToElement = this.element;
        if (scrollToElement && !isElementVisibleInViewport(scrollToElement)) {
            scrollToElement.scrollIntoView({block: 'end', inline: 'nearest', behavior: 'smooth'});
        }
    }

}
